import { create } from "zustand"

import endsWith from "lodash/endsWith"
import words from "lodash/words"

import truncate from "lodash/truncate"
import produce from "immer"
import {
  DISSECT_MODE,
  GENERAL_AREAS_MODE,
  MALE_MODEL_SEX,
  PIN_MODE,
  POSTURE_ASSESSMENT_MODE,
  SELECT_MODE,
  TAB_ONE,
  TAB_TWO,
} from "../constants/constants"
import { getFunctions, httpsCallable } from "firebase/functions"
import { getApp } from "firebase/app"
import { useUserStore } from "./useUserStore"
import { generateFirestoreId } from "../utils/nanoidUtils"
import {
  firestoreGetClientDoc,
  firestoreGetImagesFromTreatment,
  firestoreSaveSessionToFirestore,
  firestoreSetNewClientDoc,
} from "../firebase/firestore"
import { useGlobalToastStore } from "./useGlobalToastStore"
import { useImageStore } from "./useImageStore"
import { useCameraStore } from "./useCameraStore"
import { useMeshVisibilityStore } from "./useMeshVisibilityStore"
import { usePostureStore } from "./usePostureStore"
import { usePinPointStore } from "./usePinPointStore"
import { useTreatmentTechniqueStore } from "./useTreatmentTechniqueStore"

import { useConditionsSelectStore } from "./useConditionsSelectStore"
import { useSelectAreaStore } from "./useSelectAreaStore"
import { useRangeOfMovementStore } from "./useRangeOfMovementStore"
import { useSelectedMeshStore } from "./useSelectedMeshStore"
import { useAssessmentStore } from "./useAssessmentStore"

const _deactivateTechniquesIfNeeded = (newMode, keepTechniquesActive) => {
  if (newMode === GENERAL_AREAS_MODE || keepTechniquesActive) return
  const { techniquesModeActive } = useTreatmentTechniqueStore.getState()
  if (!techniquesModeActive) return
  useTreatmentTechniqueStore.setState({
    techniquesModeActive: false,
    appendAreasMode: false,
    clickedArea: null,
    clickedPosition: null,
    clickedPoint3D: null,
  })
  useMeshVisibilityStore.getState().setSystemVisibility({
    system: "visibleSkin",
    visibleState: true,
  })
  const { useDictationStore } = require("./useDictationStore")
  useDictationStore.getState().showConditionsPanel()
}

const subscribeAssessments = (sessionId) => {
  const uid = useUserStore.getState().currentUser?.uid
  if (uid && sessionId) {
    useAssessmentStore.getState().subscribeForSession({ uid, sessionId })
  }
}

//TODO: Persist useCurrentSessionStore data using persist, createJSONStorage 'zustand/middleware'

export const useCurrentSessionStore = create((set, get) => ({
  sessionDateTime: {
    date: null,
    time: null,
    dateTime: null,
  },
  sessionId: null,
  createdAt: null,
  modelSex: null,
  sessionClient: null,
  lastModified: null,
  sessionNotes: {
    tabOne: "",
    tabTwo: "",
    tabThree: "",
    tabFour: "",
    tabFive: "",
    tabSix: "",
  },
  redFlagAnswers: {
    weakness: null,
    bladderBowel: null,
    sensation: null,
    coordination: null,
  },
  currentNote: {
    uid: null,
    noteId: null,
    orderIndex: null,
    label: null,
    postureTab: null,
    docId: null,
    id: null,
    abbr: null,
    key: null,
  },
  eventMode: SELECT_MODE,
  setModelSex: (payload) => {
    set({
      modelSex: payload,
    })
  },

  addTestNote: ({ noteId, output }) => {
    const { sessionNotes } = get()
    const { userNoteSettings } = useUserStore.getState()
    const prevNote = sessionNotes[noteId]
    const newCurrentNote = userNoteSettings.find((n) => n.noteId === noteId)
    if (prevNote.length < 1) {
      set(
        produce((state) => {
          state.sessionNotes[noteId] = `${output}`
          state.currentNote = newCurrentNote
        })
      )
    } else {
      set(
        produce((state) => {
          state.sessionNotes[noteId] = `${prevNote}\n${output}`
          state.currentNote = newCurrentNote
        })
      )
    }
  },
  setSelectedClientDuringTreatment: (client) => {
    const prev = get().sessionClient
    console.log(
      `[CLIENT-SWITCH] setSelectedClientDuringTreatment: from=${prev?.clientId || "none"} to=${client?.clientId || "cleared"} (${client?.name || ""})`
    )
    console.log("[CLIENT-SWITCH] Resetting mesh visibility, camera, pin points")
    useMeshVisibilityStore.getState().resetMeshVisibilityStore()
    useCameraStore.getState().resetCameraStore()
    usePinPointStore.getState().resetPinPointStore()
    if (client) {
      set(
        produce((state) => {
          state.modelSex = client.modelSex
          state.sessionClient = {
            ...client,
          }
        })
      )
    } else {
      set({ sessionClient: null })
    }
    console.log("[CLIENT-SWITCH] setSelectedClientDuringTreatment complete")
  },
  setClientDuringSession: ({ clientDoc }) => {
    const prev = get().sessionClient
    console.log(
      `[CLIENT-SWITCH] setClientDuringSession: from=${prev?.clientId || "none"} to=${clientDoc?.clientId} (${clientDoc?.name || ""})`
    )
    set(
      produce((state) => {
        state.modelSex = clientDoc.modelSex
        state.sessionClient = {
          ...clientDoc,
        }
      })
    )
    console.log("[CLIENT-SWITCH] setClientDuringSession complete")
  },
  addGenericNoteToTab: ({ noteId, noteText }) => {
    const { sessionNotes } = get()
    const { userNoteSettings } = useUserStore.getState()
    const prevNote = sessionNotes[noteId]
    const newCurrentNote = userNoteSettings.find((n) => n.noteId === noteId)
    if (prevNote.length < 1) {
      set(
        produce((state) => {
          state.sessionNotes[noteId] = `${noteText}`
          state.currentNote = newCurrentNote
        })
      )
    } else {
      set(
        produce((state) => {
          state.sessionNotes[noteId] = `${prevNote}\n${noteText}`
          state.currentNote = newCurrentNote
        })
      )
    }
  },
  addNoteToTabSilently: ({ noteId, noteText }) => {
    const { sessionNotes } = get()
    const prevNote = sessionNotes[noteId]
    if (prevNote === undefined) return
    set(
      produce((state) => {
        state.sessionNotes[noteId] =
          prevNote.length < 1 ? noteText : `${prevNote}\n${noteText}`
      })
    )
  },
  replaceNoteLineInTab: ({ noteId, oldText, newText }) => {
    const { sessionNotes } = get()
    const prev = sessionNotes[noteId]
    if (prev === undefined || !prev.includes(oldText)) return
    set(produce((state) => {
      state.sessionNotes[noteId] = prev.replace(oldText, newText)
    }))
  },
  addSelectedName: (name) => {
    const {
      sessionNotes,
      currentNote,
    } = get()
    const { techniquesModeActive } = useTreatmentTechniqueStore.getState()
    const rxNoteId = useUserStore.getState().userNoteSettings?.find((n) => n.abbr === "Rx")?.noteId
    const noteId = (techniquesModeActive && rxNoteId) ? rxNoteId : currentNote.noteId
    const prevNote = sessionNotes[noteId]
    if (prevNote === undefined) return
    if (prevNote.length < 1) {
      set(
        produce((state) => {
          state["sessionNotes"][noteId] = `${name},`
        })
      )
    } else if (endsWith(prevNote, `${name},`)) {
      return
    } else {
      set(
        produce((state) => {
          state["sessionNotes"][noteId] = `${prevNote} ${name},`
        })
      )
    }
  },

  startNewClientSession: async ({ firstName, lastName, modelSex }) => {
    console.log(`[CLIENT-SWITCH] startNewClientSession: ${firstName} ${lastName} (${modelSex})`)
    const { userNoteSettings } = useUserStore.getState()
    const currentNote = userNoteSettings.find((n) => n.orderIndex < 1)
    const { setSuccess, setError } = useGlobalToastStore.getState()
    const newClientId = await firestoreSetNewClientDoc({
      firstName,
      lastName,
      modelSex,
    })
    // console.log(newClient)
    const newClientDoc = await firestoreGetClientDoc(newClientId)
    if (newClientId && newClientDoc) {
      setSuccess("New Client Saved!")
      const sessionId = generateFirestoreId()
      const createdAt = new Date()
      const lastModified = createdAt
      const modelSex = newClientDoc.data()?.modelSex ?? MALE_MODEL_SEX
      set(
        produce((state) => {
          state.sessionId = sessionId
          state.createdAt = createdAt
          state.modelSex = modelSex
          state.currentNote = currentNote
          state.lastModified = lastModified
          state.sessionClient = {
            ...newClientDoc.data(),
          }
          state.redFlagAnswers = {
            weakness: null,
            bladderBowel: null,
            sensation: null,
            coordination: null,
          }
          state.sessionDateTime.date = createdAt
          state.sessionDateTime.time = createdAt
          state.sessionDateTime.dateTime = createdAt
        })
      )
      subscribeAssessments(sessionId)
    } else {
      setError("Error Creating Client")
    }
  },
  startNewQuickSession: () => {
    console.log("[CLIENT-SWITCH] startNewQuickSession (no client)")
    const { userNoteSettings } = useUserStore.getState()
    const current = userNoteSettings.find((n) => n.orderIndex < 1)
    const sessionId = generateFirestoreId()
    const createdAt = new Date()
    const lastModified = createdAt

    set(
      produce((state) => {
        state.sessionId = sessionId
        state.currentNote = current
        state.createdAt = createdAt
        state.lastModified = lastModified
        state.redFlagAnswers = {
          weakness: null,
          bladderBowel: null,
          sensation: null,
          coordination: null,
        }
        state.sessionDateTime.date = createdAt
        state.sessionDateTime.time = createdAt
        state.sessionDateTime.dateTime = createdAt
      })
    )
    subscribeAssessments(sessionId)
  },
  startExistingClientSession: ({ client }) => {
    console.log(`[CLIENT-SWITCH] startExistingClientSession: clientId=${client?.clientId} (${client?.name || ""})`)
    const { userNoteSettings } = useUserStore.getState()
    const current = userNoteSettings.find((n) => n.orderIndex < 1)
    const sessionId = generateFirestoreId()
    const createdAt = new Date()
    const lastModified = createdAt
    const modelSex = client?.modelSex ?? MALE_MODEL_SEX

    set(
      produce((state) => {
        state.currentNote = current
        state.createdAt = createdAt
        state.sessionClient = client
        state.sessionId = sessionId
        state.lastModified = lastModified
        state.modelSex = modelSex
        state.redFlagAnswers = {
          weakness: null,
          bladderBowel: null,
          sensation: null,
          coordination: null,
        }
        state.sessionDateTime.date = createdAt
        state.sessionDateTime.time = createdAt
        state.sessionDateTime.dateTime = createdAt
      })
    )
    subscribeAssessments(sessionId)
  },
  setInitialCurrentNote: (currentNote) => {
    set({ currentNote })
  },
  onCurrentSessionNoteChange: ({ name, value }) => {
    set(
      produce((state) => {
        state.sessionNotes[name] = value
      })
    )
  },
  setRedFlagAnswer: (key, value) => {
    set(
      produce((state) => {
        state.redFlagAnswers[key] = value
      })
    )
  },
  handleDraftStart: async (draftInfo) => {
    console.log(`[CLIENT-SWITCH] handleDraftStart: sessionId=${draftInfo?.sessionId}, clientId=${draftInfo?.clientId || "none"}`)
    const { userNoteSettings, sessionDateTime } = draftInfo
    try {
      const images = await firestoreGetImagesFromTreatment(draftInfo.sessionId)
      if (!images.empty) {
        const data = images.docs.map((doc) => ({ ...doc.data() }))
        useImageStore.getState().setImages(data)
      }
    } catch (error) {
      console.log(error)
    }

    // Firestore Timestamp → JS Date. dateTime may be a Timestamp (post-fix) or a
    // JS Date (legacy) or null — toDate() only exists on Timestamps.
    const toJsDate = (v) => (v && typeof v.toDate === "function" ? v.toDate() : v ?? null)
    const newDateTime = {
      date: toJsDate(sessionDateTime?.date),
      time: toJsDate(sessionDateTime?.time),
      dateTime: toJsDate(sessionDateTime?.dateTime),
    }
    const firstNote = userNoteSettings.find((n) => n.orderIndex < 1)
    const newCurrentNote = useUserStore
      .getState()
      .userNoteSettings.find((n) => n.noteId === firstNote.noteId)
    set({
      redFlagAnswers: {
        weakness: null,
        bladderBowel: null,
        sensation: null,
        coordination: null,
      },
      ...draftInfo,
      sessionDateTime: newDateTime,
      currentNote: newCurrentNote,
    })
    subscribeAssessments(draftInfo.sessionId)

    // Hydrate the AI Dictation panel with any pending state from the draft.
    // Opens the panel automatically if there's any saved content.
    try {
      const { useDictationStore } = require("./useDictationStore")
      useDictationStore.getState().hydrateFromDraft({
        transcript: draftInfo.transcript,
        pendingConditions: draftInfo.pendingConditions,
        pendingSoap: draftInfo.pendingSoap,
        pendingAppliedSections: draftInfo.pendingAppliedSections,
        assessmentFindings: draftInfo.pendingAssessmentFindings,
      })
    } catch (e) {
      console.log("Dictation hydration failed:", e)
    }
  },

  setCurrentNote: (note) => {
    const { eventMode } = get()

    if (note?.postureTab) {
      set(
        produce((state) => {
          state.eventMode = POSTURE_ASSESSMENT_MODE
          state["currentNote"] = note
        })
      )
    } else if (eventMode === POSTURE_ASSESSMENT_MODE) {
      set(
        produce((state) => {
          state.eventMode = SELECT_MODE
          state["currentNote"] = note
        })
      )
    } else {
      set(
        produce((state) => {
          state["currentNote"] = note
        })
      )
    }
  },

  undoLast: (noteId) => {
    const { sessionNotes } = get()
    const prevNote = sessionNotes[noteId]

    if (prevNote.length > 0) {
      if (prevNote.endsWith(",")) {
        const w = words(prevNote, /[^,]+/g)

        const last = w[w.length - 1]
        const newNote = prevNote.replace(`${last},`, "")

        set(
          produce((state) => {
            state.sessionNotes[noteId] = `${newNote}`
          })
        )
      } else {
        const w = words(prevNote)
        const prevNoteLength = prevNote.length
        const last = w[w.length - 1]
        const lastLength = last.length + 1
        const tLength = prevNoteLength - lastLength
        const newNote = truncate(prevNote, {
          length: tLength,
          omission: "",
        })

        set(
          produce((state) => {
            state.sessionNotes[noteId] = `${newNote}`
          })
        )
      }
    }
  },

  setEventMode: (eventMode, options = {}) => {
    if (eventMode === PIN_MODE) {
      useMeshVisibilityStore
        .getState()
        .setSystemVisibility({ system: "visibleSkin", visibleState: false })
    }
    if (eventMode === GENERAL_AREAS_MODE) {
      useMeshVisibilityStore
        .getState()
        .setSystemVisibility({ system: "visibleSkin", visibleState: true })
    }
    useSelectedMeshStore.getState().clearSelectedMesh()
    _deactivateTechniquesIfNeeded(eventMode, options.keepTechniquesActive)
    // Must run after the Techniques cleanup above, which turns the skin back on.
    if (eventMode === DISSECT_MODE) {
      useMeshVisibilityStore
        .getState()
        .setSystemVisibility({ system: "visibleSkin", visibleState: false })
    }
    set({ eventMode })
  },
  setPinMode: () => {
    useMeshVisibilityStore
      .getState()
      .setSystemVisibility({ system: "visibleSkin", visibleState: true })
    set({ eventMode: PIN_MODE })
  },
  toggleSetEventMode: (mode) => {
    const { eventMode } = get()
    useSelectedMeshStore.getState().clearSelectedMesh()

    if (eventMode === mode) {
      _deactivateTechniquesIfNeeded(SELECT_MODE)
      set({ eventMode: SELECT_MODE })
    } else {
      _deactivateTechniquesIfNeeded(mode)
      set({
        eventMode: mode,
      })
    }
  },
  saveCurrentSessionAndReset: async ({ isFinal }) => {
    const { setSuccess, setError } = useGlobalToastStore.getState()

    const { userNoteSettings } = useUserStore.getState()
    const { images } = useImageStore.getState()
    const {
      createdAt,
      modelSex,
      sessionId,
      sessionNotes,
      clearAndRestartCurrrentSession,
      sessionDateTime,
      sessionClient,
      redFlagAnswers,
    } = get()
    const clientId = sessionClient?.clientId ?? null

    // Pull pending dictation state — transcript, AI suggestions, generated notes,
    // applied-tab map, assessment findings. Saved on drafts so Continue Draft can
    // hydrate everything; saved on final too so the transcript is retained for audit.
    const dictationSnapshot = (() => {
      try {
        const { useDictationStore } = require("./useDictationStore")
        const s = useDictationStore.getState()
        return {
          transcript: s.transcript || "",
          conditions: s.conditions || [],
          soap: s.soap || null,
          appliedSections: s.appliedSections || {},
          assessmentFindings: s.assessmentFindings || [],
        }
      } catch (e) {
        return { transcript: "", conditions: [], soap: null, appliedSections: {}, assessmentFindings: [] }
      }
    })()

    const { selectedTechniques } = useTreatmentTechniqueStore.getState()
    const docData = {
      sessionNotes,
      sessionDateTime,
      isFinal,
      createdAt,
      modelSex,
      sessionId,
      sessionClient,
      clientId,
      userNoteSettings,
      // Transcript persisted permanently (clinical audit/review)
      ...(dictationSnapshot.transcript ? { transcript: dictationSnapshot.transcript } : {}),
      // Pending AI state — preserved on drafts, also stored on final so it's restorable
      pendingConditions: dictationSnapshot.conditions,
      pendingSoap: dictationSnapshot.soap,
      pendingAppliedSections: dictationSnapshot.appliedSections,
      pendingAssessmentFindings: dictationSnapshot.assessmentFindings,
      sessionTechniques: selectedTechniques,
      redFlagAnswers,
    }

    // Extract symptoms from Subjective + Objective notes before saving.
    // Own try/catch: any AI hiccup or blank notes just saves an empty list [],
    // and can never block the session from saving.
    let extractedSymptoms = []
    try {
      const subjectiveText = sessionNotes[TAB_ONE] ?? ""
      const objectiveText = sessionNotes[TAB_TWO] ?? ""
      if (subjectiveText.trim().length > 0 || objectiveText.trim().length > 0) {
        const functions = getFunctions(getApp())
        const callable = httpsCallable(functions, "extractSymptoms")
        const result = await callable({ subjectiveText, objectiveText })
        const data = result?.data
        // Function returns { symptoms: [ { symptom: "..." }, ... ] }.
        // Pull out the plain text of each symptom, drop any blanks,
        // and store a clean list like ["shoulder pain", "sleep disruption"].
        const rawList = Array.isArray(data?.symptoms)
          ? data.symptoms
          : Array.isArray(data)
          ? data
          : []
        extractedSymptoms = rawList
          .map((item) => (typeof item === "string" ? item : item?.symptom))
          .filter((text) => typeof text === "string" && text.trim().length > 0)
          .map((text) => text.trim())
      }
    } catch (symptomErr) {
      console.log("Symptom extraction failed (saving empty list []):", symptomErr)
      extractedSymptoms = []
    }
    docData.extractedSymptoms = extractedSymptoms
     

    try {
      await firestoreSaveSessionToFirestore({
        images,
        docData,
      })

      // Commit pre/post assessments — flips drafts and appends per-client outcome timeline.
      // No-op if no assessment docs exist for this session.
      try {
        const sessionDate = sessionDateTime?.dateTime ?? sessionDateTime?.date ?? createdAt ?? null
        await useAssessmentStore.getState().commitForSave({
          sessionId,
          clientId,
          sessionDate,
        })
      } catch (assessmentErr) {
        console.log("Assessment commit failed:", assessmentErr)
      }

      clearAndRestartCurrrentSession()

      return setSuccess("Saved Treatment Notes!")
    } catch (error) {
      setError(error)
    }
  },
  setCurrentSessionDate: (datestamp) => {
    const date = datestamp
    set(
      produce((state) => {
        state.sessionDateTime.date = date
      })
    )
  },
  setCurrentSessionTime: (time) => {
    const date = time
    set(
      produce((state) => {
        state.sessionDateTime.time = date
      })
    )
  },
  setCurrentSessionDateTime: (time) => {
    const date = time
    set(
      produce((state) => {
        state.sessionDateTime.dateTime = date
        state.sessionDateTime.time = date
        state.sessionDateTime.date = date
      })
    )
  },
  clearCurrentSessionDateTime: () => {
    set({
      sessionDateTime: {
        date: null,
        time: null,
        dateTime: null,
      },
    })
  },
  resetCurrentSession: () => {
    const currentNote =
      useUserStore
        .getState()
        ?.userNoteSettings?.find((n) => n.orderIndex < 1) ?? null
    set({
      sessionDateTime: {
        date: null,
        time: null,
        dateTime: null,
      },
      sessionId: null,
      createdAt: null,
      modelSex: null,
      sessionClient: null,
      lastModified: null,

      sessionNotes: {
        tabOne: "",
        tabTwo: "",
        tabThree: "",
        tabFour: "",
        tabFive: "",
        tabSix: "",
      },
      redFlagAnswers: {
        weakness: null,
        bladderBowel: null,
        sensation: null,
        coordination: null,
      },
      currentNote,

      eventMode: SELECT_MODE,
    })
  },
  clearAndRestartCurrrentSession: () => {
    const { resetCurrentSession } = get()
    useMeshVisibilityStore.getState().resetMeshVisibilityStore()
    useImageStore.getState().resetImageStore()
    useSelectedMeshStore.getState().clearSelectedMesh()
    usePostureStore.getState().resetPostureAssessmentStore()
    useCameraStore.getState().resetCameraStore()
    useRangeOfMovementStore.getState().resetRom()
    useConditionsSelectStore.getState().resetConditionsSelectStore()
    useSelectAreaStore.getState().resetAreaSelectStore()
    usePinPointStore.getState().resetPinPointStore()
    useAssessmentStore.getState().resetAssessmentStore()
    // Reset dictation state
    const { useDictationStore } = require("./useDictationStore")
    useDictationStore.getState().closeDictation()
    // Clear highlighted areas
    const { useHighlightedAreasStore } = require("./useHighlightedAreasStore")
    useHighlightedAreasStore.getState().clearHighlightedAreas()
    const { useTreatmentTechniqueStore } = require("./useTreatmentTechniqueStore")
    useTreatmentTechniqueStore.getState().clearSelectedTechniques()
    resetCurrentSession()
  },
}))
